import React from "react";
import { ExternalLink, Eye, MousePointerClick, Pencil } from "lucide-react";
import { Button, Modal, cx } from "../../../pages/admin/_shared";
import { resolveAssetDisplayUrl } from "../../../lib/assetUrl";
import { sanitizeHtml } from "../../../lib/sanitizeHtml";
import { STATUS_META, completenessOf, statusOf } from "./workflow";

const assetSrc = (a) => (typeof a === "string" ? a : a?.url ? resolveAssetDisplayUrl(a.url, a.key) : "");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("vi-VN") : "");
const fmtMoney = (n) => `${Number(n).toLocaleString("vi-VN")}₫`;
const fmtPrice = (loc) => {
  const min = Number(loc.priceMin ?? loc.price ?? 0);
  const max = Number(loc.priceMax ?? 0);
  if (!min && !max) return "";
  if (min && max && min !== max) return `${fmtMoney(min)} – ${fmtMoney(max)}`;
  return fmtMoney(min || max);
};

const Section = ({ title, children, right }) => (
  <section className="rounded-2xl border border-admin-border bg-admin-surface p-4">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

const Row = ({ label, children, mono }) => (
  <div className="min-w-0">
    <dt className="text-[11px] font-medium text-admin-textMuted">{label}</dt>
    <dd className={cx("mt-0.5 break-words text-sm text-admin-text", mono && "font-mono text-xs")}>
      {children === 0 || children ? children : <span className="text-admin-textMuted">—</span>}
    </dd>
  </div>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl bg-admin-muted/60 px-3 py-2.5">
    <Icon size={18} className="shrink-0 text-admin-primary" />
    <div>
      <p className="text-lg font-semibold tabular-nums leading-5 text-admin-text">{Number(value || 0).toLocaleString("vi-VN")}</p>
      <p className="text-[11px] text-admin-textMuted">{label}</p>
    </div>
  </div>
);

const ImageGrid = ({ items }) => (
  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
    {items.map((a, i) => {
      const src = assetSrc(a);
      return src ? (
        <a key={`${src}-${i}`} href={src} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg ring-1 ring-admin-border">
          <img src={src} alt={a?.alt || ""} loading="lazy" className="aspect-square w-full object-cover transition hover:scale-105" />
        </a>
      ) : null;
    })}
  </div>
);

const List = ({ items }) => (
  <ul className="list-inside list-disc space-y-1 text-sm text-admin-text">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

/** Xem toàn bộ thông tin một địa điểm (chỉ đọc). Dữ liệu lấy từ dòng danh sách — API list trả đủ bản ghi. */
export default function LocationDetailModal({ loc, categories = [], onClose, onEdit }) {
  if (!loc) return null;
  const st = statusOf(loc);
  const meta = STATUS_META[st] || STATUS_META.draft;
  const cover = assetSrc(loc.image);
  const hero = (loc.heroGallery || []).filter((a) => assetSrc(a));
  const gallery = (loc.gallery || []).filter((a) => assetSrc(a));
  const videos = (loc.videos || []).filter((v) => v?.url);
  const blocks = Array.isArray(loc.mediaBlocks) ? loc.mediaBlocks : [];
  const faq = (loc.faq || []).filter((f) => f?.question);
  const highlights = (loc.highlights || []).filter(Boolean);
  const tips = (loc.tips || []).filter(Boolean);
  const comp = completenessOf(loc);
  const catName = loc.category?.name || categories.find((c) => c._id === String(loc.category?._id || loc.category))?.name || loc.categoryName;
  const address = [loc.ward, loc.city].filter(Boolean).join(", ");
  const hasCoords = Number.isFinite(Number(loc.latitude)) && Number.isFinite(Number(loc.longitude));

  return (
    <Modal
      open
      onClose={onClose}
      maxWidthClass="max-w-4xl"
      title={<span className="text-base font-semibold text-admin-text">Chi tiết địa điểm</span>}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {st === "published" && loc.slug ? (
            <a href={`/location/${loc.slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" icon={ExternalLink}>Xem trên website</Button>
            </a>
          ) : null}
          {onEdit ? <Button variant="secondary" icon={Pencil} onClick={() => onEdit(loc)}>Chỉnh sửa</Button> : null}
          <Button variant="primary" onClick={onClose}>Đóng</Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Đầu trang: ảnh bìa + tên + trạng thái */}
        <div className="flex flex-col gap-4 sm:flex-row">
          {cover ? (
            <img src={cover} alt="" className="h-40 w-full shrink-0 rounded-2xl object-cover ring-1 ring-admin-border sm:w-64" />
          ) : (
            <div className="flex h-40 w-full shrink-0 items-center justify-center rounded-2xl bg-admin-muted text-xs text-admin-textMuted ring-1 ring-admin-border sm:w-64">Chưa có ảnh đại diện</div>
          )}
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-xl font-semibold text-admin-text">{loc.name}</h2>
            <p className="font-mono text-xs text-admin-textMuted">/{loc.slug || "—"}</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset", meta.badge)}>
                <span className={cx("h-1.5 w-1.5 rounded-full", meta.dot)} />
                {meta.label}
              </span>
              {catName ? <span className="rounded-md bg-admin-muted px-2 py-0.5 text-xs font-medium text-admin-text">{catName}</span> : null}
              <span className="text-xs text-admin-textMuted">Hoàn thiện {comp.percent}%</span>
            </div>
            {loc.shortIntro ? <p className="text-sm text-admin-text">{loc.shortIntro}</p> : null}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Stat icon={Eye} label="Lượt xem" value={loc.viewCount} />
              <Stat icon={MousePointerClick} label="Lượt bấm link trang chủ" value={loc.homeLinkClicks} />
            </div>
          </div>
        </div>

        {comp.missing.length ? (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">Còn thiếu: {comp.missing.map((m) => m.label).join(", ")}</p>
        ) : null}

        <Section title="Thông tin cơ bản">
          <dl className="grid gap-4 sm:grid-cols-3">
            <Row label="Địa chỉ">{address}</Row>
            <Row label="Số điện thoại">{loc.phone}</Row>
            <Row label="Giá">{fmtPrice(loc)}</Row>
            <Row label="Giờ mở cửa">{loc.openTime || loc.closeTime ? `${loc.openTime || "—"} – ${loc.closeTime || "—"}` : ""}</Row>
            <Row label="Toạ độ" mono>{hasCoords ? `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)}` : ""}</Row>
            <Row label="Icon bản đồ">{loc.mapIcon}</Row>
          </dl>
          {hasCoords ? (
            <a
              href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
            >
              <ExternalLink size={12} /> Mở trên Google Maps
            </a>
          ) : null}
        </Section>

        <Section title="Mô tả">
          {loc.description ? (
            <div className="prose prose-sm max-w-none text-admin-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(loc.description) }} />
          ) : (
            <p className="text-sm text-admin-textMuted">Chưa có mô tả.</p>
          )}
        </Section>

        {highlights.length || tips.length ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {highlights.length ? <Section title="Điểm nổi bật"><List items={highlights} /></Section> : null}
            {tips.length ? <Section title="Mẹo tham quan"><List items={tips} /></Section> : null}
          </div>
        ) : null}

        {hero.length ? <Section title={`Ảnh bìa trang chi tiết (${hero.length})`}><ImageGrid items={hero} /></Section> : null}
        {gallery.length ? <Section title={`Thư viện ảnh (${gallery.length})`}><ImageGrid items={gallery} /></Section> : null}

        {videos.length || blocks.length || loc.tiktokUrl ? (
          <Section title="Video & nội dung media">
            <ul className="space-y-1.5 text-sm">
              {videos.map((v, i) => (
                <li key={`v-${i}`}>
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-admin-primary hover:underline">{v.title || `Video ${i + 1}`}</a>
                </li>
              ))}
              {loc.tiktokUrl ? (
                <li>
                  TikTok: <a href={loc.tiktokUrl} target="_blank" rel="noopener noreferrer" className="break-all text-admin-primary hover:underline">{loc.tiktokUrl}</a>
                </li>
              ) : null}
              {blocks.length ? (
                <li className="text-admin-textMuted">
                  {blocks.length} khối nội dung ({["image", "video", "text"].map((t) => `${blocks.filter((b) => b?.type === t).length} ${t === "image" ? "ảnh" : t === "video" ? "video" : "chữ"}`).join(", ")})
                </li>
              ) : null}
            </ul>
          </Section>
        ) : null}

        {faq.length ? (
          <Section title={`Câu hỏi thường gặp (${faq.length})`}>
            <dl className="space-y-3">
              {faq.map((f, i) => (
                <div key={i}>
                  <dt className="text-sm font-semibold text-admin-text">{f.question}</dt>
                  <dd className="mt-0.5 text-sm text-admin-textMuted">{f.answer}</dd>
                </div>
              ))}
            </dl>
          </Section>
        ) : null}

        <Section title="SEO & liên kết">
          <dl className="grid gap-4 sm:grid-cols-2">
            <Row label="Tiêu đề SEO">{loc.seoTitle}</Row>
            <Row label="Từ khoá">{(loc.seoKeywords || []).join(", ")}</Row>
            <div className="sm:col-span-2"><Row label="Mô tả SEO">{loc.seoDescription}</Row></div>
            <Row label="Link trang chủ">{loc.homeLinkUrl}</Row>
            <Row label="Nhãn link">{loc.homeLinkLabel}</Row>
          </dl>
        </Section>

        <Section title="Lịch sử">
          <dl className="grid gap-4 sm:grid-cols-3">
            <Row label="Ngày tạo">{fmtDateTime(loc.createdAt)}</Row>
            <Row label="Cập nhật lần cuối">{fmtDateTime(loc.updatedAt)}</Row>
            <Row label="Ngày đăng">{fmtDateTime(loc.publishedAt)}</Row>
            <Row label="ID" mono>{loc._id}</Row>
          </dl>
        </Section>
      </div>
    </Modal>
  );
}
